import React, { useState, useEffect } from "react";
import { resolveUploadUrl } from "../../utils/apiClient";
import {
  getEmployees, 
  inviteEmployee, 
  cancelInvite,
  fetchJobTitles, 
  createJobTitle,
  updateEmployee,
  getAllPermissions,
  archiveEmployee,
  restoreEmployee,
  uploadEmployeeAvatar,
  createPlaceholderEmployee,
  updatePlaceholderEmployee,
  deletePlaceholderEmployee
} from "../../services/peopleService";
import {
  getResources,
  createResource,
  updateResource,
  archiveResource,
  restoreResource
} from "../../services/resourceService";
import {
  getEmployeeEntitlements,
  createEntitlement,
  updateEntitlement,
  deleteEntitlement,
  generateDefaultEntitlements
} from "../../services/leaveEntitlementService";
import { getTimeOffTypes, createTimeOffType } from "../../services/timeOffService";
import { getTeamRoles, createTeamRole } from "../../services/teamRoleService";
import { useCurrentUser } from "../../context/UserContext";
import { useCurrency } from "../../hooks/useCurrency";
import { usePermissions } from "../../hooks/usePermission";
import FormModal from "../../components/forms/FormModal";
import DetailsModal from "../../components/forms/DetailsModal";
import PermissionSelector from "../../components/forms/PermissionSelector";
import ConfirmDialog from "../../components/ConfirmDialog";
import Toast from "../../components/Toast";
import ui from "./DashboardUI.module.css";
import detailStyles from "../../components/forms/DetailsModal.module.css";
import useMinimumLoader from "../../hooks/useMinimumLoader";
import peopleStyles from "./people/PeopleUI.module.css";
import PeopleToolbar from "./people/PeopleToolbar";
import AddDropdown from "./people/AddDropdown";
import SectionHeader from "./people/SectionHeader";
import SubsectionHeader from "./people/SubsectionHeader";
import PersonCompactCard from "./people/PersonCompactCard";
import PersonRow, { PersonListHeader } from "./people/PersonRow";
import ResourceCompactCard from "./people/ResourceCompactCard";
import ResourceRow, { ResourceListHeader } from "./people/ResourceRow";

export default function People() {
  const currentUser = useCurrentUser();
  const { format: formatMoney, symbol: currencySymbol } = useCurrency();
  const { canInvitePeople, canEditPeople, canEditPermissions, canDeletePeople, canViewResources, canEditResources, canDeleteResources, canAccessTimeOff } = usePermissions();
  const [users, setUsers] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [teamRoles, setTeamRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const showLoading = useMinimumLoader(loading);
  const [toast, setToast] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelInviteConfirm, setShowCancelInviteConfirm] = useState(false);

  // Leave entitlement state
  const [editTab, setEditTab] = useState('details');
  const [entitlements, setEntitlements] = useState([]);
  const [entitlementsLoading, setEntitlementsLoading] = useState(false);
  const [timeOffTypes, setTimeOffTypes] = useState([]);
  const [showAddEntitlementModal, setShowAddEntitlementModal] = useState(false);
  const [editingEntitlement, setEditingEntitlement] = useState(null);
  const [showNewLeaveTypeModal, setShowNewLeaveTypeModal] = useState(false);
  const [newLeaveTypeForm, setNewLeaveTypeForm] = useState({ name: '', description: '', default_allocation: '' });
  const [entitlementForm, setEntitlementForm] = useState({
    time_off_type_id: '',
    total_days: '',
    carry_over_days: '0',
    period_start: `${new Date().getFullYear()}-01-01`,
    period_end: `${new Date().getFullYear()}-12-31`,
    notes: ''
  });

  // Resource state
  const [resources, setResources] = useState([]);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [showResourceDetailModal, setShowResourceDetailModal] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showArchivedResources, setShowArchivedResources] = useState(false);
  const [showArchivedPeople, setShowArchivedPeople] = useState(false);
  const [resourceForm, setResourceForm] = useState({
    name: "",
    type: "misc",
    description: "",
    availability_status: "available",
    asset_value: "",
    running_cost: "",
    depreciation_rate: "",
    cost_center: "",
    registration_number: "",
    vehicle_type: "",
    capacity: "",
    insurance_expiry: "",
    inspection_expiry: "",
  });

  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showNewJobTitleModal, setShowNewJobTitleModal] = useState(false);
  const [newJobTitleName, setNewJobTitleName] = useState("");
  const [jobTitleTarget, setJobTitleTarget] = useState(null); // 'invite' or 'edit'
  const [avatarFile, setAvatarFile] = useState(null); // For editing employee avatar
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone_number: "",
    default_hourly_rate: "",
    default_weekly_hours: "",
    title_id: "",
    permissions: [],
  });
  const [showPlaceholderModal, setShowPlaceholderModal] = useState(false);
  const [showAdvancedPermissions, setShowAdvancedPermissions] = useState(false);
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const [newRoleForm, setNewRoleForm] = useState({
    role_name: "",
    description: "",
    permissions: [],
  });
  const [placeholderForm, setPlaceholderForm] = useState({
    name: "",
    phone_number: "",
    description: "",
    default_hourly_rate: "",
    default_weekly_hours: "",
  });
  const [editingPlaceholder, setEditingPlaceholder] = useState(null);

  // View / search / collapse state for new layout
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('peoplePageViewMode') || 'grid'; }
    catch { return 'grid'; }
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem('peoplePageCollapsed') || '{}'); }
    catch { return {}; }
  });

  useEffect(() => {
    try { localStorage.setItem('peoplePageViewMode', viewMode); } catch {}
  }, [viewMode]);

  useEffect(() => {
    try { localStorage.setItem('peoplePageCollapsed', JSON.stringify(collapsedSections)); } catch {}
  }, [collapsedSections]);

  const toggleSection = (key) =>
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const matchesQuery = (text) =>
    !searchQuery || (text || '').toString().toLowerCase().includes(searchQuery.toLowerCase());

  const filterPerson = (p) =>
    matchesQuery(p.name) ||
    matchesQuery(p.email) ||
    matchesQuery(p.phone_number) ||
    matchesQuery(p.job_title) ||
    matchesQuery(p.role_name);

  const filterResource = (r) =>
    matchesQuery(r.name) ||
    matchesQuery(r.type) ||
    matchesQuery(r.availability_status) ||
    matchesQuery(r.registration_number) ||
    matchesQuery(r.cost_center);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const realEmployees = users.filter((user) => user.kind !== 'ghost');
  const contractorEmployees = users.filter((user) => user.kind === 'ghost');
  const toolResources = resources.filter((resource) => resource.type !== 'vehicle');
  const vehicleResources = resources.filter((resource) => resource.type === 'vehicle');

  const realEmployeesFiltered = realEmployees.filter(filterPerson);
  const contractorEmployeesFiltered = contractorEmployees.filter(filterPerson);
  const toolResourcesFiltered = toolResources.filter(filterResource);
  const vehicleResourcesFiltered = vehicleResources.filter(filterResource);

  const peopleSearchHidesAll =
    searchQuery && realEmployeesFiltered.length === 0 && contractorEmployeesFiltered.length === 0;
  const resourcesSearchHidesAll =
    searchQuery && toolResourcesFiltered.length === 0 && vehicleResourcesFiltered.length === 0;

  // Direct edit/delete helpers used by row action menus (skip detail modal)
  const editEmployeeDirect = (emp) => {
    if (!emp) return;
    if (emp.kind === 'ghost') {
      setEditingPlaceholder(emp);
      return;
    }
    const [firstName = '', lastName = ''] = (emp.name || '').split(' ');
    setEditingEmployee({
      ...emp,
      firstName,
      lastName,
      permissions: emp.permissions || [],
    });
    setEditTab('details');
    setShowAdvancedPermissions(false);
  };

  const requestDeleteEmployee = (emp) => {
    setSelectedEmployee(emp);
    setShowDeleteConfirm(true);
  };

  const buildPersonActions = (emp) => {
    const actions = [];
    actions.push({ label: 'View details', onClick: () => openEmployeeDetail(emp) });
    if (canEditPeople) {
      actions.push({ label: 'Edit', onClick: () => editEmployeeDirect(emp) });
    }
    if (canDeletePeople) {
      const archivable = emp.kind === 'real' || emp.kind === 'ghost';
      if (archivable && emp.is_active === false) {
        actions.push({ label: 'Restore', onClick: () => handleRestoreEmployee(emp) });
      } else if (archivable) {
        actions.push({ label: 'Archive', onClick: () => requestDeleteEmployee(emp), danger: true });
      } else {
        actions.push({ label: 'Delete', onClick: () => requestDeleteEmployee(emp), danger: true });
      }
    }
    return actions;
  };

  const buildResourceActions = (resource) => {
    const actions = [];
    actions.push({ label: 'View details', onClick: () => openResourceDetail(resource) });
    if (canEditResources) {
      actions.push({ label: 'Edit', onClick: () => setEditingResource({ ...resource }) });
    }
    if (canDeleteResources) {
      if (resource.is_active) {
        actions.push({
          label: 'Archive',
          onClick: () => { setSelectedResource(resource); setShowArchiveConfirm(true); },
          danger: true,
        });
      } else {
        actions.push({ label: 'Restore', onClick: () => handleRestoreResource(resource) });
      }
    }
    return actions;
  };

  // Map backend status -> frontend 'kind' used for filtering. Archived
  // employees keep their real/ghost kind (and the is_active flag) so they
  // can still be shown and restored.
  const mapEmployees = (list) => (list || []).map((emp) => ({
    ...emp,
    kind: emp.status === 'invited'
      ? 'invited'
      : (emp.kind === 'ghost' || emp.status === 'placeholder')
        ? 'ghost'
        : 'real',
  }));

  const refreshEmployees = async () => {
    const updated = await getEmployees(showArchivedPeople);
    setUsers(mapEmployees(updated?.employees || []));
  };

  useEffect(() => {
    async function loadData() {
      if (!currentUser) return;

      try {
        const [employeesData, titlesData, permissionsData, rolesData] = await Promise.all([
          getEmployees(showArchivedPeople),
          fetchJobTitles(),
          canEditPermissions ? getAllPermissions() : Promise.resolve({ permissions: {} }),
          canEditPermissions ? getTeamRoles().catch(() => []) : Promise.resolve([]),
        ]);

        setUsers(mapEmployees(employeesData?.employees || []));
        setJobTitles(titlesData || []);
        setAvailablePermissions(permissionsData?.permissions || {});
        setTeamRoles(rolesData || []);

        // Load resources if user has permission
        if (canViewResources) {
          try {
            const resourcesData = await getResources(showArchivedResources);
            setResources(resourcesData?.resources || []);
          } catch (err) {
            console.error('Error loading resources:', err);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [currentUser, canInvitePeople, canEditPermissions, canViewResources, showArchivedResources, showArchivedPeople]);

  // Load time off types once
  useEffect(() => {
    if (canAccessTimeOff) {
      getTimeOffTypes().then(data => setTimeOffTypes(data || [])).catch(console.error);
    }
  }, [canAccessTimeOff]);

  // Fetch entitlements when employee detail modal opens on Time Off tab
  const fetchEntitlements = async (employeeId) => {
    setEntitlementsLoading(true);
    try {
      const data = await getEmployeeEntitlements(employeeId);
      setEntitlements(data || []);
    } catch (err) {
      console.error('Error loading entitlements:', err);
      setEntitlements([]);
    } finally {
      setEntitlementsLoading(false);
    }
  };

  const handleEditTabChange = (tab) => {
    setEditTab(tab);
    if (tab === 'timeoff' && editingEmployee) {
      fetchEntitlements(editingEmployee.user_id);
    }
  };

  const handleGenerateDefaults = async () => {
    if (!editingEmployee) return;
    try {
      await generateDefaultEntitlements(editingEmployee.user_id);
      await fetchEntitlements(editingEmployee.user_id);
      showToast('Default entitlements generated successfully.', 'success');
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Failed to generate defaults.', 'error');
    }
  };

  const handleAddEntitlement = async (e) => {
    e.preventDefault();
    if (!editingEmployee) return;
    try {
      await createEntitlement({
        employee_id: editingEmployee.user_id,
        time_off_type_id: entitlementForm.time_off_type_id,
        total_days: parseFloat(entitlementForm.total_days) || 0,
        carry_over_days: parseFloat(entitlementForm.carry_over_days) || 0,
        period_start: entitlementForm.period_start,
        period_end: entitlementForm.period_end,
        notes: entitlementForm.notes
      });
      await fetchEntitlements(editingEmployee.user_id);
      setShowAddEntitlementModal(false);
      setEntitlementForm({
        time_off_type_id: '',
        total_days: '',
        carry_over_days: '0',
        period_start: `${new Date().getFullYear()}-01-01`,
        period_end: `${new Date().getFullYear()}-12-31`,
        notes: ''
      });
      showToast('Entitlement added successfully.', 'success');
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.error || err.message || 'Failed to add entitlement.', 'error');
    }
  };

  const handleUpdateEntitlement = async (e) => {
    e.preventDefault();
    if (!editingEntitlement || !editingEmployee) return;
    try {
      await updateEntitlement(editingEntitlement.entitlement_id, {
        total_days: parseFloat(editingEntitlement.total_days) || 0,
        carry_over_days: parseFloat(editingEntitlement.carry_over_days) || 0,
        period_start: editingEntitlement.period_start,
        period_end: editingEntitlement.period_end,
        notes: editingEntitlement.notes
      });
      await fetchEntitlements(editingEmployee.user_id);
      setEditingEntitlement(null);
      showToast('Entitlement updated successfully.', 'success');
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Failed to update entitlement.', 'error');
    }
  };

  const handleDeleteEntitlement = async (entitlementId) => {
    if (!editingEmployee) return;
    try {
      await deleteEntitlement(entitlementId);
      await fetchEntitlements(editingEmployee.user_id);
      showToast('Entitlement deleted successfully.', 'success');
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Failed to delete entitlement.', 'error');
    }
  };

  const handleCreateLeaveType = async (e) => {
    e.preventDefault();
    if (!newLeaveTypeForm.name.trim()) {
      showToast('Leave type name is required.', 'error');
      return;
    }
    try {
      await createTimeOffType({
        name: newLeaveTypeForm.name.trim(),
        description: newLeaveTypeForm.description.trim(),
        default_allocation: newLeaveTypeForm.default_allocation ? parseInt(newLeaveTypeForm.default_allocation) : null
      });
      const updated = await getTimeOffTypes();
      setTimeOffTypes(updated || []);
      setShowNewLeaveTypeModal(false);
      setNewLeaveTypeForm({ name: '', description: '', default_allocation: '' });
      showToast('Leave type created successfully.', 'success');
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Failed to create leave type.', 'error');
    }
  };

  const handleTeamRoleChange = (roleId) => {
    if (!roleId) {
      return;
    }

    // Check if user wants to create a new role
    if (roleId === "create-new-role") {
      setShowCreateRoleModal(true);
      return;
    }

    const selectedRole = teamRoles.find(role => role.role_id === parseInt(roleId));
    if (selectedRole && selectedRole.permissions) {
      setForm((prev) => ({ ...prev, permissions: selectedRole.permissions }));
    }
  };

  const handleEditTeamRoleChange = (roleId) => {
    if (!roleId) {
      return;
    }

    if (roleId === "create-new-role") {
      setShowCreateRoleModal(true);
      return;
    }

    const selectedRole = teamRoles.find(role => role.role_id === parseInt(roleId));
    if (selectedRole && selectedRole.permissions) {
      setEditingEmployee((prev) => ({ ...prev, permissions: selectedRole.permissions }));
    }
  };

  const handleCreateNewRole = async (e) => {
    e.preventDefault();
    
    if (!newRoleForm.role_name || newRoleForm.role_name.trim() === "") {
      showToast("Please enter a role name.", "error");
      return;
    }

    try {
      const roleData = {
        role_name: newRoleForm.role_name,
        description: newRoleForm.description,
        permissions: newRoleForm.permissions || [],
        is_default: false
      };

      const createdRole = await createTeamRole(roleData);
      showToast(`Role "${createdRole.role_name}" created successfully!`, "success");
      
      // Refresh team roles list
      const rolesData = await getTeamRoles();
      setTeamRoles(rolesData || []);
      
      // Apply the new role's permissions to the invite form
      setForm((prev) => ({ ...prev, permissions: createdRole.permissions || [] }));
      
      // Reset and close modal
      setNewRoleForm({
        role_name: "",
        description: "",
        permissions: [],
      });
      setShowCreateRoleModal(false);
    } catch (error) {
      showToast(error.message || "Failed to create role", "error");
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();

    if (!form.email || form.email.trim() === "") {
      showToast("Please enter an email address.", "error");
      return;
    }

    if (!form.title_id || form.title_id === "") {
      showToast("Please select a job title.", "error");
      return;
    }

    const formData = { 
      ...form,
      default_hourly_rate: form.default_hourly_rate ? parseFloat(form.default_hourly_rate) : null,
      default_weekly_hours: form.default_weekly_hours ? parseFloat(form.default_weekly_hours) : 40,
      permissions: form.permissions || [],
    };     

    try {
      
      setShowInviteForm(false);
      await inviteEmployee(formData);

      showToast("Employee invitation sent successfully.", "success");

      const updated = await getEmployees();
      
      setUsers(updated.employees || updated || []);

      setForm({
        firstName: "",
        lastName: "",
        email: "",
        title_id: "",
        permissions: [],
      });

      setShowInviteForm(false);
      showToast("Employee invitation sent successfully.", "success");

    } catch (err) {
      console.error(err);
      const errorMessage = err.message || err.error || "Failed to send invite.";
      showToast(errorMessage, "error");
    }
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    try {
      const updatedEmployee = {
        ...editingEmployee,
        name: `${editingEmployee.firstName} ${editingEmployee.lastName}`.trim(),
        default_hourly_rate: editingEmployee.default_hourly_rate === '' || editingEmployee.default_hourly_rate === null || editingEmployee.default_hourly_rate === undefined
          ? null
          : parseFloat(editingEmployee.default_hourly_rate),
        default_weekly_hours: editingEmployee.default_weekly_hours === '' || editingEmployee.default_weekly_hours === null || editingEmployee.default_weekly_hours === undefined
          ? null
          : parseFloat(editingEmployee.default_weekly_hours),
      };

      await updateEmployee(updatedEmployee);

      // Upload avatar if a file was selected
      if (avatarFile) {
        await uploadEmployeeAvatar(editingEmployee.user_id, avatarFile);
      }

      const updated = await getEmployees();
      
      setUsers(updated.employees || updated || []);
      setEditingEmployee(null);
      setAvatarFile(null); // Reset avatar file
      setShowAdvancedPermissions(false); // Reset advanced permissions state
      showToast("Employee updated successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to update employee.", "error");
    }
  };

  const openEmployeeDetail = (employee) => {
    setSelectedEmployee(employee);
    setEntitlements([]);
    setShowDetailModal(true);
  };

  const handleEditFromDetail = () => {
    if (selectedEmployee) {
      const [firstName = "", lastName = ""] = selectedEmployee.name.split(" ");
      setEditingEmployee({ 
        ...selectedEmployee, 
        firstName, 
        lastName,
        permissions: selectedEmployee.permissions || []
      });
      setEditTab('details');
      setShowAdvancedPermissions(false); // Reset advanced permissions state
      setShowDetailModal(false);
    }
  };

  const handleDeleteEmployee = async () => {
    if (!selectedEmployee) return;
    
    if (selectedEmployee.kind === 'ghost') {
      await handleDeletePlaceholder();
      return;
    }

    try {
      await archiveEmployee(selectedEmployee.user_id);

      await refreshEmployees();

      setShowDeleteConfirm(false);
      setShowDetailModal(false);
      setSelectedEmployee(null);
      showToast("Employee archived successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to archive employee.", "error");
      setShowDeleteConfirm(false);
    }
  };

  const handleRestoreEmployee = async (emp) => {
    try {
      await restoreEmployee(emp.user_id);
      await refreshEmployees();
      showToast("Employee restored successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to restore employee.", "error");
    }
  };

  const handleCancelInvite = async () => {
    if (!selectedEmployee || !selectedEmployee.invite_id) return;
    
    try {
      await cancelInvite(selectedEmployee.invite_id);
      
      const updated = await getEmployees();
      setUsers(updated.employees || updated || []);
      
      setShowCancelInviteConfirm(false);
      setShowDetailModal(false);
      setSelectedEmployee(null);
      showToast("Invitation cancelled successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to cancel invitation.", "error");
      setShowCancelInviteConfirm(false);
    }
  };

  // Handle create placeholder
  const handleCreatePlaceholder = async (e) => {
    e.preventDefault();
    if (!placeholderForm.name.trim()) {
      showToast("Name is required for a contractor.", "error");
      return;
    }
    try {
      await createPlaceholderEmployee({
        name: placeholderForm.name.trim(),
        phone_number: placeholderForm.phone_number || null,
        description: placeholderForm.description || null,
        default_hourly_rate: placeholderForm.default_hourly_rate || null,
        default_weekly_hours: placeholderForm.default_weekly_hours || null
      });
      setShowPlaceholderModal(false);
      setPlaceholderForm({
        name: "",
        phone_number: "",
        description: "",
        default_hourly_rate: "",
        default_weekly_hours: "",
      });
      const updated = await getEmployees();
      setUsers(updated.employees || updated || []);
      showToast("Contractor employee created successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to create contractor employee.", "error");
    }
  };

  const handleCreateJobTitle = async () => {
    if (!newJobTitleName.trim()) {
      showToast("Job title name is required.", "error");
      return;
    }

    try {
      const newJobTitle = await createJobTitle(newJobTitleName.trim());
      
      // Add to job titles list
      setJobTitles(prev => [...prev, newJobTitle]);
      
      // Set the selected value based on target
      if (jobTitleTarget === 'invite') {
        setForm(prev => ({ ...prev, title_id: newJobTitle.title_id }));
      } else if (jobTitleTarget === 'edit') {
        setEditingEmployee(prev => ({ ...prev, title_id: newJobTitle.title_id }));
      }
      
      // Reset modal state
      setShowNewJobTitleModal(false);
      setNewJobTitleName("");
      setJobTitleTarget(null);
      
      showToast("Job title created successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to create job title.", "error");
    }
  };

  // Get job title name from title_id
  const getJobTitleName = (titleId) => {
    const title = jobTitles.find(jt => jt.title_id === titleId);
    return title ? title.name : "—";
  };

  // Handle edit placeholder from detail
  const handleEditPlaceholderFromDetail = () => {
    if (selectedEmployee && selectedEmployee.kind === 'ghost') {
      setEditingPlaceholder(selectedEmployee);
      setShowDetailModal(false);
    }
  };

  // Handle update placeholder
  const handleUpdatePlaceholder = async (e) => {
    e.preventDefault();
    if (!editingPlaceholder.name.trim()) {
      showToast("Name is required.", "error");
      return;
    }
    try {
      await updatePlaceholderEmployee(editingPlaceholder.user_id, {
        name: editingPlaceholder.name.trim(),
        phone_number: editingPlaceholder.phone_number || null,
        description: editingPlaceholder.description || null,
        default_hourly_rate: editingPlaceholder.default_hourly_rate || null,
        default_weekly_hours: editingPlaceholder.default_weekly_hours || null
      });
      setEditingPlaceholder(null);
      const updated = await getEmployees();
      setUsers(updated.employees || updated || []);
      showToast("Contractor employee updated successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to update contractor employee.", "error");
    }
  };

  // Handle archive placeholder
  const handleDeletePlaceholder = async () => {
    if (!selectedEmployee || selectedEmployee.kind !== 'ghost') return;
    try {
      await deletePlaceholderEmployee(selectedEmployee.user_id);
      await refreshEmployees();
      setShowDeleteConfirm(false);
      setShowDetailModal(false);
      setSelectedEmployee(null);
      showToast("Contractor archived successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to archive contractor.", "error");
      setShowDeleteConfirm(false);
    }
  };

  // ---- Resource Handlers ----
  const resetResourceForm = () => {
    setResourceForm({
      name: "",
      type: "misc",
      description: "",
      availability_status: "available",
      asset_value: "",
      running_cost: "",
      depreciation_rate: "",
      cost_center: "",
      registration_number: "",
      vehicle_type: "",
      capacity: "",
      insurance_expiry: "",
      inspection_expiry: "",
    });
  };

  const refreshResources = async () => {
    try {
      const resourcesData = await getResources(showArchivedResources);
      setResources(resourcesData?.resources || []);
    } catch (err) {
      console.error('Error refreshing resources:', err);
    }
  };

  const handleCreateResource = async (e) => {
    e.preventDefault();
    if (!resourceForm.name.trim()) {
      showToast("Resource name is required.", "error");
      return;
    }
    try {
      await createResource(resourceForm);
      setShowResourceModal(false);
      resetResourceForm();
      await refreshResources();
      showToast("Resource created successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to create resource.", "error");
    }
  };

  const handleUpdateResource = async (e) => {
    e.preventDefault();
    if (!editingResource || !editingResource.name.trim()) {
      showToast("Resource name is required.", "error");
      return;
    }
    try {
      await updateResource(editingResource.resource_id, editingResource);
      setEditingResource(null);
      await refreshResources();
      showToast("Resource updated successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to update resource.", "error");
    }
  };

  const handleArchiveResource = async () => {
    if (!selectedResource) return;
    try {
      await archiveResource(selectedResource.resource_id);
      setShowArchiveConfirm(false);
      setShowResourceDetailModal(false);
      setSelectedResource(null);
      await refreshResources();
      showToast("Resource archived successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to archive resource.", "error");
      setShowArchiveConfirm(false);
    }
  };

  const handleRestoreResource = async (resource) => {
    try {
      await restoreResource(resource.resource_id);
      await refreshResources();
      showToast("Resource restored successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to restore resource.", "error");
    }
  };

  const openResourceDetail = (resource) => {
    setSelectedResource(resource);
    setShowResourceDetailModal(true);
  };

  const handleEditResourceFromDetail = () => {
    if (selectedResource) {
      setEditingResource({ ...selectedResource });
      setShowResourceDetailModal(false);
    }
  };

  const getResourceTypeLabel = (type) => {
    const labels = { misc: 'Miscellaneous', vehicle: 'Vehicle', tool: 'Tool / Equipment' };
    return labels[type] || type;
  };

  const getAvailabilityLabel = (status) => {
    const labels = { available: 'Available', in_use: 'In Use', maintenance: 'Maintenance', unavailable: 'Unavailable' };
    return labels[status] || status;
  };

  const getAvailabilityColor = (status) => {
    const colors = { available: '#16a34a', in_use: '#2563eb', maintenance: '#d97706', unavailable: '#dc2626' };
    return colors[status] || '#6b7280';
  };

  const getResourceFormFields = (formState, setFormState) => {
    const fields = [
      {
        label: "Name",
        value: formState.name,
        onChange: (e) => setFormState((prev) => ({ ...prev, name: e.target.value })),
        placeholder: "Enter resource name...",
      },
      {
        label: "Type",
        type: "select",
        value: formState.type,
        onChange: (e) => setFormState((prev) => ({ ...prev, type: e.target.value })),
        options: [
          { value: "misc", label: "Miscellaneous" },
          { value: "vehicle", label: "Vehicle" },
          { value: "tool", label: "Tool / Equipment" },
        ],
      },
      {
        label: "Availability",
        type: "select",
        value: formState.availability_status,
        onChange: (e) => setFormState((prev) => ({ ...prev, availability_status: e.target.value })),
        options: [
          { value: "available", label: "Available" },
          { value: "in_use", label: "In Use" },
          { value: "maintenance", label: "Maintenance" },
          { value: "unavailable", label: "Unavailable" },
        ],
      },
      {
        label: "Description",
        type: "textarea",
        value: formState.description,
        onChange: (e) => setFormState((prev) => ({ ...prev, description: e.target.value })),
        placeholder: "Enter description...",
      },
    ];

    // Vehicle-specific fields
    if (formState.type === "vehicle") {
      fields.push(
        { type: "divider", label: "Vehicle Details" },
        {
          label: "Registration Number",
          value: formState.registration_number,
          onChange: (e) => setFormState((prev) => ({ ...prev, registration_number: e.target.value })),
          placeholder: "e.g., ABC 123 GP",
        },
        {
          label: "Vehicle Type",
          type: "select",
          value: formState.vehicle_type,
          onChange: (e) => setFormState((prev) => ({ ...prev, vehicle_type: e.target.value })),
          options: [
            { value: "", label: "Select vehicle type..." },
            { value: "truck", label: "Truck" },
            { value: "bakkie", label: "Bakkie" },
            { value: "van", label: "Van" },
            { value: "car", label: "Car" },
            { value: "trailer", label: "Trailer" },
            { value: "other", label: "Other" },
          ],
        },
        {
          label: "Capacity",
          value: formState.capacity,
          onChange: (e) => setFormState((prev) => ({ ...prev, capacity: e.target.value })),
          placeholder: "e.g., 5 tons, 8 passengers",
        },
        {
          type: "row",
          fields: [
            {
              label: "Insurance Expiry",
              type: "date",
              value: formState.insurance_expiry ? formState.insurance_expiry.split('T')[0] : "",
              onChange: (e) => setFormState((prev) => ({ ...prev, insurance_expiry: e.target.value })),
            },
            {
              label: "Inspection Expiry",
              type: "date",
              value: formState.inspection_expiry ? formState.inspection_expiry.split('T')[0] : "",
              onChange: (e) => setFormState((prev) => ({ ...prev, inspection_expiry: e.target.value })),
            },
          ],
        }
      );
    }

    // Accounting fields
    fields.push(
      { type: "divider", label: "Accounting" },
      {
        type: "row",
        fields: [
          {
            label: `Asset Value (${currencySymbol})`,
            type: "number",
            step: "0.01",
            min: "0",
            placeholder: "0.00",
            value: formState.asset_value,
            onChange: (e) => setFormState((prev) => ({ ...prev, asset_value: e.target.value })),
          },
          {
            label: `Running Cost (${currencySymbol})`,
            type: "number",
            step: "0.01",
            min: "0",
            placeholder: "0.00",
            value: formState.running_cost,
            onChange: (e) => setFormState((prev) => ({ ...prev, running_cost: e.target.value })),
          },
        ],
      },
      {
        type: "row",
        fields: [
          {
            label: "Depreciation Rate (%)",
            type: "number",
            step: "0.01",
            min: "0",
            max: "100",
            placeholder: "0.00",
            value: formState.depreciation_rate,
            onChange: (e) => setFormState((prev) => ({ ...prev, depreciation_rate: e.target.value })),
          },
          {
            label: "Cost Center",
            value: formState.cost_center,
            onChange: (e) => setFormState((prev) => ({ ...prev, cost_center: e.target.value })),
            placeholder: "e.g., Operations",
          },
        ],
      }
    );

    return fields;
  };

  if (showLoading) {
    return (
      <div className={ui.pageContainer}>
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
        <div className={ui.peopleSkeletonWrap}>
          <div className={ui.peopleSkeletonHeader}>
            <div className={ui.peopleSkeletonTitle} />
            <div className={ui.peopleSkeletonAddBtn} />
          </div>

          <div className={ui.peopleSkeletonSection}>
            <div className={ui.peopleSkeletonSectionHeader}>
              <div className={ui.peopleSkeletonSectionTitle} />
              <div className={ui.peopleSkeletonBadge} />
            </div>
            <div className={ui.peopleSkeletonCardGrid}>
              {Array.from({ length: 4 }).map((_, idx) => (
                <div key={`people-employee-card-skeleton-${idx}`} className={ui.peopleSkeletonCard}>
                  <div className={ui.peopleSkeletonAvatar} />
                  <div className={ui.peopleSkeletonLineLg} />
                  <div className={ui.peopleSkeletonLineMd} />
                </div>
              ))}
            </div>
          </div>

          <div className={ui.peopleSkeletonSection}>
            <div className={ui.peopleSkeletonSectionHeader}>
              <div className={ui.peopleSkeletonSectionTitle} />
              <div className={ui.peopleSkeletonBadge} />
            </div>
            <div className={ui.peopleSkeletonTableLike}>
              <div className={ui.peopleSkeletonTableHeader} />
              {Array.from({ length: 5 }).map((_, idx) => (
                <div key={`people-resource-row-skeleton-${idx}`} className={ui.peopleSkeletonTableRow} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={ui.pageContainer}>
      <div className={ui.fadeInContent}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Top bar */}
      <div className={ui.pageHeader}>
        <div className={ui.pageHeaderLeft}>
          <h1 className={ui.pageTitle}>People & Resources</h1>
        </div>
        <div className={ui.pageHeaderRight}>
          {canInvitePeople && (
            <AddDropdown
              canViewResources={canViewResources}
              onAddEmployee={() => setShowInviteForm(true)}
              onAddContractor={() => setShowPlaceholderModal(true)}
              onAddResource={() => setShowResourceModal(true)}
            />
          )}
        </div>
      </div>

      <PeopleToolbar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        showArchived={showArchivedResources || showArchivedPeople}
        onToggleArchived={(checked) => {
          setShowArchivedResources(checked);
          setShowArchivedPeople(checked);
        }}
        canShowArchived={canViewResources || canDeletePeople}
      />

      {/* Invite Modal */}
      {showInviteForm && (
        <FormModal
          title="Invite Employee"
          onCancel={() => {
            setShowInviteForm(false);
            setForm({
              firstName: "",
              lastName: "",
              email: "",
              phone_number: "",
              default_hourly_rate: "",
              default_weekly_hours: "",
              title_id: "",
              permissions: [],
            });
          }}
          onSubmit={handleInvite}
          fields={[
            {
              type: "row",
              fields: [
                {
                  label: "First Name",
                  value: form.firstName,
                  onChange: (e) =>
                    setForm((prev) => ({ ...prev, firstName: e.target.value })),
                },
                {
                  label: "Last Name",
                  value: form.lastName,
                  onChange: (e) =>
                    setForm((prev) => ({ ...prev, lastName: e.target.value })),
                },
              ],
            },
            {
              label: "Email",
              type: "email",
              value: form.email,
              onChange: (e) =>
                setForm((prev) => ({ ...prev, email: e.target.value })),
            },
            {
              label: "Job Title",
              type: "select",
              value: form.title_id,
              onChange: (e) => {
                if (e.target.value === "add-new-job-title") {
                  setJobTitleTarget('invite');
                  setShowNewJobTitleModal(true);
                } else {
                  setForm((prev) => ({ ...prev, title_id: e.target.value }));
                }
              },
              options: [
                { value: "", label: "Select a job title..." },
                { value: "add-new-job-title", label: "➕ Add New Job Title..." },
                ...(jobTitles || []).map((jt) => ({
                  value: jt.title_id,
                  label: jt.name,
                })),
              ],
            },
            ...(canEditPermissions && teamRoles.length > 0 ? [{
              label: "Team Role (Optional)",
              type: "select",
              value: "",
              onChange: (e) => handleTeamRoleChange(e.target.value),
              options: [
                { value: "", label: "Select a role to apply permissions..." },
                { value: "create-new-role", label: "➕ Create New Role..." },
                ...(teamRoles || []).map((role) => ({
                  value: role.role_id,
                  label: `${role.role_name}${role.is_default ? ' (Default)' : ''} - ${role.permissions?.length || 0} permissions`,
                })),
              ],
            }] : []),
            ...(canEditPermissions ? [{
              type: "custom",
              render: () => (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowAdvancedPermissions(!showAdvancedPermissions)}
                    className={ui.advancedPermissionsToggle}
                  >
                    {showAdvancedPermissions ? '▼' : '▶'}
                    <span>Advanced Permissions - {showAdvancedPermissions ? 'Hide' : 'Show'} Manual Selection</span>
                  </button>
                  {showAdvancedPermissions && (
                    <PermissionSelector
                      permissions={availablePermissions}
                      selectedPermissions={form.permissions}
                      onChange={(newPermissions) =>
                        setForm((prev) => ({ ...prev, permissions: newPermissions }))
                      }
                    />
                  )}
                </div>
              ),
            }] : []),
          ]}
        />
      )}

      {/* Placeholder Modal */}
      {showPlaceholderModal && (
        <FormModal
          title="Add Contractor"
          onCancel={() => {
            setShowPlaceholderModal(false);
            setPlaceholderForm({
              name: "",
              phone_number: "",
              description: "",
              default_hourly_rate: "",
              default_weekly_hours: "",
            });
          }}
          onSubmit={handleCreatePlaceholder}
          fields={[
            {
              label: "Name",
              value: placeholderForm.name,
              onChange: (e) =>
                setPlaceholderForm((prev) => ({ ...prev, name: e.target.value })),
              placeholder: "Enter contractor name...",
            },
            {
              label: "Phone Number",
              type: "tel",
              value: placeholderForm.phone_number,
              onChange: (e) =>
                setPlaceholderForm((prev) => ({ ...prev, phone_number: e.target.value })),
              placeholder: "+27 12 345 6789",
            },
            {
              label: "Description",
              type: "textarea",
              value: placeholderForm.description,
              onChange: (e) =>
                setPlaceholderForm((prev) => ({ ...prev, description: e.target.value })),
              placeholder: "Enter description...",
            },
            {
              type: "row",
              fields: [
                {
                  label: `Hourly Rate (${currencySymbol})`,
                  type: "number",
                  step: "0.01",
                  min: "0",
                  placeholder: "0.00",
                  value: placeholderForm.default_hourly_rate,
                  onChange: (e) =>
                    setPlaceholderForm((prev) => ({ ...prev, default_hourly_rate: e.target.value })),
                },
                {
                  label: "Weekly Hours",
                  type: "number",
                  step: "0.5",
                  min: "0",
                  placeholder: "40",
                  value: placeholderForm.default_weekly_hours,
                  onChange: (e) =>
                    setPlaceholderForm((prev) => ({ ...prev, default_weekly_hours: e.target.value })),
                },
              ],
            },
          ]}
        />
      )}

      {/* Create New Role Modal */}
      {showCreateRoleModal && (
        <FormModal
          title="Create New Team Role"
          onCancel={() => {
            setShowCreateRoleModal(false);
            setNewRoleForm({
              role_name: "",
              description: "",
              permissions: [],
            });
          }}
          onSubmit={handleCreateNewRole}
          fields={[
            {
              label: "Role Name",
              value: newRoleForm.role_name,
              onChange: (e) =>
                setNewRoleForm((prev) => ({ ...prev, role_name: e.target.value })),
              placeholder: "e.g., Project Manager, Developer, Viewer",
            },
            {
              label: "Description",
              type: "textarea",
              value: newRoleForm.description,
              onChange: (e) =>
                setNewRoleForm((prev) => ({ ...prev, description: e.target.value })),
              placeholder: "Describe this role and when to use it",
            },
            {
              label: "Permissions",
              type: "custom",
              render: () => (
                <PermissionSelector
                  permissions={availablePermissions}
                  selectedPermissions={newRoleForm.permissions}
                  onChange={(newPermissions) =>
                    setNewRoleForm((prev) => ({ ...prev, permissions: newPermissions }))
                  }
                />
              ),
            },
          ]}
        />
      )}

      {editingPlaceholder && (
        <FormModal
          title="Edit Contractor"
          onCancel={() => {
            setEditingPlaceholder(null);
          }}
          onSubmit={handleUpdatePlaceholder}
          fields={[
            {
              label: "Name",
              value: editingPlaceholder.name,
              onChange: (e) =>
                setEditingPlaceholder((prev) => ({ ...prev, name: e.target.value })),
              placeholder: "Enter contractor name...",
            },
            {
              label: "Phone Number",
              type: "tel",
              value: editingPlaceholder.phone_number || "",
              onChange: (e) =>
                setEditingPlaceholder((prev) => ({ ...prev, phone_number: e.target.value })),
              placeholder: "+27 12 345 6789",
            },
            {
              label: "Description",
              type: "textarea",
              value: editingPlaceholder.description || "",
              onChange: (e) =>
                setEditingPlaceholder((prev) => ({ ...prev, description: e.target.value })),
              placeholder: "Enter description...",
            },
            {
              type: "row",
              fields: [
                {
                  label: `Hourly Rate (${currencySymbol})`,
                  type: "number",
                  step: "0.01",
                  min: "0",
                  placeholder: "0.00",
                  value: editingPlaceholder.default_hourly_rate || "",
                  onChange: (e) =>
                    setEditingPlaceholder((prev) => ({ ...prev, default_hourly_rate: e.target.value })),
                },
                {
                  label: "Weekly Hours",
                  type: "number",
                  step: "0.5",
                  min: "0",
                  placeholder: "40",
                  value: editingPlaceholder.default_weekly_hours || "",
                  onChange: (e) =>
                    setEditingPlaceholder((prev) => ({ ...prev, default_weekly_hours: e.target.value })),
                },
              ],
            },
          ]}
        />
      )}

      {editingEmployee && canEditPeople && (
        <FormModal
          title="Edit Employee"
          onCancel={() => {
            setEditingEmployee(null);
            setAvatarFile(null);
            setEditTab('details');
            setShowAdvancedPermissions(false);
          }}
          onSubmit={editTab === 'details' ? handleUpdateEmployee : null}
          tabs={canAccessTimeOff ? [
            { key: 'details', label: 'Details' },
            { key: 'timeoff', label: 'Time Off' }
          ] : null}
          activeTab={editTab}
          onTabChange={handleEditTabChange}
          customTabContent={editTab === 'timeoff' ? (
            <div>
              {/* Summary Cards */}
              {entitlements.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '20px' }}>
                  {[
                    { label: 'Available', value: entitlements.reduce((s, e) => s + parseFloat(e.total_available || 0), 0), color: '#6366f1' },
                    { label: 'Approved', value: entitlements.reduce((s, e) => s + parseFloat(e.days_approved || 0), 0), color: '#16a34a' },
                    { label: 'Pending', value: entitlements.reduce((s, e) => s + parseFloat(e.days_pending || 0), 0), color: '#d97706' },
                    { label: 'Remaining', value: entitlements.reduce((s, e) => s + parseFloat(e.days_remaining || 0), 0), color: '#2563eb' },
                  ].map((card) => (
                    <div key={card.label} style={{ textAlign: 'center', padding: '10px 6px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <div style={{ fontSize: '1.2rem', fontWeight: '700', color: card.color }}>{card.value}</div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '2px' }}>{card.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button
                  type="button"
                  onClick={handleGenerateDefaults}
                  style={{ padding: '6px 12px', fontSize: '0.8rem', backgroundColor: '#6366f1', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                >
                  Generate Defaults
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddEntitlementModal(true)}
                  style={{ padding: '6px 12px', fontSize: '0.8rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}
                >
                  + Add Entitlement
                </button>
              </div>

              {/* Entitlement Table */}
              {entitlementsLoading ? (
                <p style={{ color: '#9ca3af', textAlign: 'center', padding: '20px' }}>Loading entitlements...</p>
              ) : entitlements.length === 0 ? (
                <p style={{ color: '#9ca3af', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
                  No leave entitlements found. Click "Generate Defaults" to create entitlements from company leave types.
                </p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ textAlign: 'left', padding: '8px 6px', color: '#6b7280', fontWeight: '600', fontSize: '0.75rem', textTransform: 'uppercase' }}>Leave Type</th>
                        <th style={{ textAlign: 'center', padding: '8px 4px', color: '#6b7280', fontWeight: '600', fontSize: '0.75rem' }}>Total</th>
                        <th style={{ textAlign: 'center', padding: '8px 4px', color: '#6b7280', fontWeight: '600', fontSize: '0.75rem' }}>Carry Over</th>
                        <th style={{ textAlign: 'center', padding: '8px 4px', color: '#6b7280', fontWeight: '600', fontSize: '0.75rem' }}>Approved</th>
                        <th style={{ textAlign: 'center', padding: '8px 4px', color: '#6b7280', fontWeight: '600', fontSize: '0.75rem' }}>Pending</th>
                        <th style={{ textAlign: 'center', padding: '8px 4px', color: '#6b7280', fontWeight: '600', fontSize: '0.75rem' }}>Remaining</th>
                        <th style={{ textAlign: 'center', padding: '8px 4px', color: '#6b7280', fontWeight: '600', fontSize: '0.75rem' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entitlements.map((ent) => {
                        const remaining = parseFloat(ent.days_remaining || 0);
                        const total = parseFloat(ent.total_available || 0);
                        const isLow = total > 0 && remaining / total < 0.2;
                        return (
                          <tr key={ent.entitlement_id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: isLow ? '#fef2f2' : 'transparent' }}>
                            <td style={{ padding: '8px 6px', fontWeight: '500', color: '#111827' }}>
                              {ent.time_off_type_name}
                              <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                                {new Date(ent.period_start).toLocaleDateString()} → {new Date(ent.period_end).toLocaleDateString()}
                              </div>
                            </td>
                            <td style={{ textAlign: 'center', padding: '8px 4px' }}>{parseFloat(ent.total_days)}</td>
                            <td style={{ textAlign: 'center', padding: '8px 4px' }}>{parseFloat(ent.carry_over_days)}</td>
                            <td style={{ textAlign: 'center', padding: '8px 4px', color: '#16a34a' }}>{parseFloat(ent.days_approved)}</td>
                            <td style={{ textAlign: 'center', padding: '8px 4px', color: '#d97706' }}>{parseFloat(ent.days_pending)}</td>
                            <td style={{ textAlign: 'center', padding: '8px 4px', fontWeight: '600', color: isLow ? '#dc2626' : '#2563eb' }}>{remaining}</td>
                            <td style={{ textAlign: 'center', padding: '8px 4px' }}>
                              <button
                                type="button"
                                onClick={() => setEditingEntitlement({ ...ent, period_start: ent.period_start?.split('T')[0], period_end: ent.period_end?.split('T')[0] })}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: '0.8rem', marginRight: '4px' }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteEntitlement(ent.entitlement_id)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '0.8rem' }}
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}
          fields={[
            {
              label: "Avatar",
              type: "custom",
              render: () => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: '#f3f4f6',
                    border: '2px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#6b7280',
                    flexShrink: 0,
                    overflow: 'hidden'
                  }}>
                    {editingEmployee.avatar_url ? (
                      <img 
                        src={resolveUploadUrl(editingEmployee.avatar_url)}
                        alt={editingEmployee.name}
                        style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                      />
                    ) : (
                      editingEmployee.name.split(' ').map(n => n[0]).join('').toUpperCase()
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setAvatarFile(e.target.files[0])}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }}
                    />
                    <p style={{ 
                      margin: '4px 0 0 0', 
                      fontSize: '12px', 
                      color: '#6b7280',
                      fontStyle: 'italic'
                    }}>
                      Select a new image to update the avatar (max 2MB)
                    </p>
                  </div>
                </div>
              ),
            },
            {
              type: "row",
              fields: [
                {
                  label: "First Name",
                  value: editingEmployee.firstName,
                  onChange: (e) =>
                    setEditingEmployee((prev) => ({ ...prev, firstName: e.target.value })),
                },
                {
                  label: "Last Name",
                  value: editingEmployee.lastName,
                  onChange: (e) =>
                    setEditingEmployee((prev) => ({ ...prev, lastName: e.target.value })),
                },
              ],
            },
            {
              label: "Email",
              type: "email",
              value: editingEmployee.email,
              onChange: (e) =>
                setEditingEmployee((prev) => ({ ...prev, email: e.target.value })),
            },
            {
              label: "Phone Number",
              type: "tel",
              value: editingEmployee.phone_number || "",
              placeholder: "+27 123 4567",
              onChange: (e) =>
                setEditingEmployee((prev) => ({ ...prev, phone_number: e.target.value })),
            },
            {
              label: "Job Title",
              type: "select",
              value: editingEmployee.title_id,
              onChange: (e) => {
                if (e.target.value === "add-new-job-title") {
                  setJobTitleTarget('edit');
                  setShowNewJobTitleModal(true);
                } else {
                  setEditingEmployee((prev) => ({ ...prev, title_id: e.target.value }));
                }
              },
              options: [
                { value: "add-new-job-title", label: "➕ Add New Job Title..." },
                ...(jobTitles || []).map((jt) => ({
                  value: jt.title_id,
                  label: jt.name,
                })),
              ],
            },
            {
              type: "row",
              fields: [
                {
                  label: `Hourly Rate (${currencySymbol})`,
                  type: "number",
                  step: "0.01",
                  min: "0",
                  placeholder: "0.00",
                  value: editingEmployee.default_hourly_rate || "",
                  onChange: (e) =>
                    setEditingEmployee((prev) => ({ ...prev, default_hourly_rate: e.target.value })),
                },
                {
                  label: "Weekly Hours",
                  type: "number",
                  step: "0.5",
                  min: "0",
                  placeholder: "40",
                  value: editingEmployee.default_weekly_hours || "",
                  onChange: (e) =>
                    setEditingEmployee((prev) => ({ ...prev, default_weekly_hours: e.target.value })),
                },
              ],
            },
            ...(canEditPermissions && teamRoles.length > 0 ? [{
              label: "Team Role (Optional)",
              type: "select",
              value: "",
              onChange: (e) => handleEditTeamRoleChange(e.target.value),
              options: [
                { value: "", label: "Select a role to apply permissions..." },
                { value: "create-new-role", label: "➕ Create New Role..." },
                ...(teamRoles || []).map((role) => ({
                  value: role.role_id,
                  label: `${role.role_name}${role.is_default ? ' (Default)' : ''} - ${role.permissions?.length || 0} permissions`,
                })),
              ],
            }] : []),
            ...(canEditPermissions ? [{
              type: "custom",
              render: () => (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowAdvancedPermissions(!showAdvancedPermissions)}
                    className={ui.advancedPermissionsToggle}
                  >
                    {showAdvancedPermissions ? '▼' : '▶'}
                    <span>Advanced Permissions - {showAdvancedPermissions ? 'Hide' : 'Show'} Manual Selection</span>
                  </button>
                  {showAdvancedPermissions && (
                    <PermissionSelector
                      permissions={availablePermissions}
                      selectedPermissions={editingEmployee.permissions || []}
                      onChange={(newPermissions) =>
                        setEditingEmployee((prev) => ({ ...prev, permissions: newPermissions }))
                      }
                    />
                  )}
                </div>
              ),
            }] : []),
          ]}
        />
      )}

      {/* Employee Detail Modal */}
      {showDetailModal && selectedEmployee && (
        <DetailsModal
          title="Employee Details"
          onClose={() => {
            setShowDetailModal(false);
            setSelectedEmployee(null);
          }}
          onEdit={selectedEmployee.kind === 'ghost' ? (canEditPeople ? handleEditPlaceholderFromDetail : null) : (selectedEmployee.status === 'invited' ? null : (canEditPeople ? handleEditFromDetail : null))}
          onDelete={
            selectedEmployee.kind === 'ghost'
              ? (canDeletePeople ? () => setShowDeleteConfirm(true) : null)
              : (selectedEmployee.status === 'invited' 
                  ? (canInvitePeople ? () => setShowCancelInviteConfirm(true) : null) 
                  : (canDeletePeople ? () => setShowDeleteConfirm(true) : null))
          }
          deleteLabel={selectedEmployee.kind === 'ghost' ? "Delete Contractor" : (selectedEmployee.status === 'invited' ? "Cancel Invitation" : "Remove Employee")}
          editLabel={selectedEmployee.kind === 'ghost' ? "Edit Contractor" : "Edit Employee"}
        >
          {/* Profile Header */}
          <div className={detailStyles.detailSection}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '20px',
              padding: '20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px',
              marginBottom: '8px'
            }}>
              <div style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                border: '3px solid rgba(255,255,255,0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                fontWeight: '600',
                color: 'white',
                flexShrink: 0
              }}>
                {selectedEmployee.avatar_url ? (
                  <img 
                    src={resolveUploadUrl(selectedEmployee.avatar_url)}
                    alt={selectedEmployee.name}
                    style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  selectedEmployee.name.split(' ').map(n => n[0]).join('').toUpperCase()
                )}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '1.35rem', fontWeight: '600', color: 'white' }}>
                  {selectedEmployee.name}
                </h3>
                <p style={{ margin: '6px 0 0', color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
                  {selectedEmployee.job_title || getJobTitleName(selectedEmployee.title_id) || 'No title assigned'}
                </p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className={detailStyles.detailSection}>
            <h4 className={detailStyles.sectionTitle}>Contact Information</h4>
            
            <div className={detailStyles.detailRow}>
              <span className={detailStyles.detailLabel}>Email</span>
              <span className={`${detailStyles.detailValue} ${!selectedEmployee.email ? detailStyles.empty : ''}`}>
                {selectedEmployee.email ? (
                  <a href={`mailto:${selectedEmployee.email}`} style={{ color: '#6366f1', textDecoration: 'none' }}>
                    {selectedEmployee.email}
                  </a>
                ) : '—'}
              </span>
            </div>

            <div className={detailStyles.detailRow}>
              <span className={detailStyles.detailLabel}>Phone</span>
              <span className={`${detailStyles.detailValue} ${!(selectedEmployee.phone_number || selectedEmployee.phone) ? detailStyles.empty : ''}`}>
                {selectedEmployee.phone_number || selectedEmployee.phone || '—'}
              </span>
            </div>
          </div>

          {/* Work Details */}
          <div className={detailStyles.detailSection}>
            <h4 className={detailStyles.sectionTitle}>Work Details</h4>
            
            <div className={detailStyles.detailRow}>
              <span className={detailStyles.detailLabel}>Job Title</span>
              <span className={`${detailStyles.detailValue} ${!(selectedEmployee.job_title || getJobTitleName(selectedEmployee.title_id)) ? detailStyles.empty : ''}`}>
                {selectedEmployee.job_title || getJobTitleName(selectedEmployee.title_id) || '—'}
              </span>
            </div>

            <div className={detailStyles.detailRow}>
              <span className={detailStyles.detailLabel}>Hourly Rate</span>
              <span className={`${detailStyles.detailValue} ${!selectedEmployee.default_hourly_rate ? detailStyles.empty : ''}`}>
                {selectedEmployee.default_hourly_rate ? (
                  <span style={{ 
                    backgroundColor: '#dcfce7', 
                    color: '#166534', 
                    padding: '4px 10px', 
                    borderRadius: '6px',
                    fontWeight: '500',
                    fontSize: '0.9rem'
                  }}>
                    {formatMoney(selectedEmployee.default_hourly_rate)}/hr
                  </span>
                ) : '—'}
              </span>
            </div>

            <div className={detailStyles.detailRow}>
              <span className={detailStyles.detailLabel}>Weekly Hours</span>
              <span className={detailStyles.detailValue}>
                <span style={{ 
                  backgroundColor: '#dbeafe', 
                  color: '#1e40af', 
                  padding: '4px 10px', 
                  borderRadius: '6px',
                  fontWeight: '500',
                  fontSize: '0.9rem'
                }}>
                  {selectedEmployee.default_weekly_hours || '40'} hrs/week
                </span>
              </span>
            </div>

            {selectedEmployee.kind === 'ghost' && selectedEmployee.description && (
              <div className={detailStyles.detailRow}>
                <span className={detailStyles.detailLabel}>Description</span>
                <span className={detailStyles.detailValue}>
                  {selectedEmployee.description}
                </span>
              </div>
            )}
          </div>

          {/* Permissions */}
          <div className={detailStyles.detailSection}>
            <h4 className={detailStyles.sectionTitle}>
              Permissions 
              {selectedEmployee.permissions && selectedEmployee.permissions.length > 0 && (
                <span style={{ 
                  marginLeft: '8px', 
                  backgroundColor: '#e5e7eb', 
                  color: '#374151', 
                  padding: '2px 8px', 
                  borderRadius: '10px',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}>
                  {selectedEmployee.permissions.length}
                </span>
              )}
            </h4>
            
            {selectedEmployee.permissions && selectedEmployee.permissions.length > 0 ? (
              <div className={detailStyles.labelsList}>
                {selectedEmployee.permissions.map((perm, idx) => (
                  <span
                    key={idx}
                    className={detailStyles.labelTag}
                    style={{
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      padding: '6px 12px',
                      borderRadius: '6px'
                    }}
                  >
                    {perm.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            ) : (
              <p style={{ color: '#9ca3af', fontStyle: 'italic', margin: 0 }}>
                No permissions assigned
              </p>
            )}
          </div>
        </DetailsModal>
      )}

      {/* Add Entitlement Modal */}
      {showAddEntitlementModal && (
        <FormModal
          title="Add Leave Entitlement"
          onCancel={() => setShowAddEntitlementModal(false)}
          onSubmit={handleAddEntitlement}
          fields={[
            {
              label: "Leave Type",
              type: "select",
              value: entitlementForm.time_off_type_id,
              onChange: (e) => {
                if (e.target.value === '__add_new__') {
                  setShowNewLeaveTypeModal(true);
                } else {
                  setEntitlementForm(prev => ({ ...prev, time_off_type_id: e.target.value }));
                }
              },
              options: [
                { value: '', label: 'Select leave type...' },
                ...timeOffTypes.map(t => ({ value: t.time_off_type_id, label: t.name })),
                { value: '__add_new__', label: '➕ Add New Leave Type...' }
              ]
            },
            {
              type: "row",
              fields: [
                {
                  label: "Total Days",
                  type: "number",
                  step: "0.5",
                  min: "0",
                  value: entitlementForm.total_days,
                  onChange: (e) => setEntitlementForm(prev => ({ ...prev, total_days: e.target.value })),
                },
                {
                  label: "Carry Over Days",
                  type: "number",
                  step: "0.5",
                  min: "0",
                  value: entitlementForm.carry_over_days,
                  onChange: (e) => setEntitlementForm(prev => ({ ...prev, carry_over_days: e.target.value })),
                },
              ],
            },
            {
              type: "row",
              fields: [
                {
                  label: "Period Start",
                  type: "date",
                  value: entitlementForm.period_start,
                  onChange: (e) => setEntitlementForm(prev => ({ ...prev, period_start: e.target.value })),
                },
                {
                  label: "Period End",
                  type: "date",
                  value: entitlementForm.period_end,
                  onChange: (e) => setEntitlementForm(prev => ({ ...prev, period_end: e.target.value })),
                },
              ],
            },
            {
              label: "Notes",
              type: "textarea",
              value: entitlementForm.notes,
              onChange: (e) => setEntitlementForm(prev => ({ ...prev, notes: e.target.value })),
              placeholder: "Optional notes...",
            },
          ]}
        />
      )}

      {/* Edit Entitlement Modal */}
      {editingEntitlement && (
        <FormModal
          title="Edit Leave Entitlement"
          onCancel={() => setEditingEntitlement(null)}
          onSubmit={handleUpdateEntitlement}
          fields={[
            {
              label: "Leave Type",
              type: "text",
              value: editingEntitlement.time_off_type_name || '',
              onChange: () => {},
              disabled: true,
            },
            {
              type: "row",
              fields: [
                {
                  label: "Total Days",
                  type: "number",
                  step: "0.5",
                  min: "0",
                  value: editingEntitlement.total_days,
                  onChange: (e) => setEditingEntitlement(prev => ({ ...prev, total_days: e.target.value })),
                },
                {
                  label: "Carry Over Days",
                  type: "number",
                  step: "0.5",
                  min: "0",
                  value: editingEntitlement.carry_over_days,
                  onChange: (e) => setEditingEntitlement(prev => ({ ...prev, carry_over_days: e.target.value })),
                },
              ],
            },
            {
              type: "row",
              fields: [
                {
                  label: "Period Start",
                  type: "date",
                  value: editingEntitlement.period_start,
                  onChange: (e) => setEditingEntitlement(prev => ({ ...prev, period_start: e.target.value })),
                },
                {
                  label: "Period End",
                  type: "date",
                  value: editingEntitlement.period_end,
                  onChange: (e) => setEditingEntitlement(prev => ({ ...prev, period_end: e.target.value })),
                },
              ],
            },
            {
              label: "Notes",
              type: "textarea",
              value: editingEntitlement.notes || '',
              onChange: (e) => setEditingEntitlement(prev => ({ ...prev, notes: e.target.value })),
              placeholder: "Optional notes...",
            },
          ]}
        />
      )}

      {/* New Leave Type Modal */}
      {showNewLeaveTypeModal && (
        <FormModal
          title="Add New Leave Type"
          onCancel={() => {
            setShowNewLeaveTypeModal(false);
            setNewLeaveTypeForm({ name: '', description: '', default_allocation: '' });
          }}
          onSubmit={handleCreateLeaveType}
          fields={[
            {
              label: "Name",
              type: "text",
              value: newLeaveTypeForm.name,
              onChange: (e) => setNewLeaveTypeForm(prev => ({ ...prev, name: e.target.value })),
              placeholder: "e.g. Annual Leave, Sick Leave",
            },
            {
              label: "Default Allocation (days/year)",
              type: "number",
              min: "0",
              step: "1",
              value: newLeaveTypeForm.default_allocation,
              onChange: (e) => setNewLeaveTypeForm(prev => ({ ...prev, default_allocation: e.target.value })),
              placeholder: "e.g. 25",
            },
            {
              label: "Description",
              type: "textarea",
              value: newLeaveTypeForm.description,
              onChange: (e) => setNewLeaveTypeForm(prev => ({ ...prev, description: e.target.value })),
              placeholder: "Optional description...",
            },
          ]}
        />
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && selectedEmployee && (
        <ConfirmDialog
          message={
            selectedEmployee.kind === 'ghost'
              ? `Archive ${selectedEmployee.name}? Their timesheets and bookings are kept and they can be restored later, but they'll be removed from active lists and excluded from billing.`
              : `Archive ${selectedEmployee.name}? Their timesheets, bookings and history are kept and they can be restored later, but they'll be removed from active lists, excluded from billing, and unable to log in.`
          }
          confirmText="Archive"
          cancelText="Cancel"
          onConfirm={handleDeleteEmployee}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {/* Cancel Invite Confirmation */}
      {showCancelInviteConfirm && selectedEmployee && (
        <ConfirmDialog
          message={`Are you sure you want to cancel the invitation for ${selectedEmployee.name}?`}
          confirmText="Cancel Invitation"
          cancelText="Keep Invitation"
          onConfirm={handleCancelInvite}
          onCancel={() => setShowCancelInviteConfirm(false)}
        />
      )}

      {/* New Job Title Modal */}
      {showNewJobTitleModal && (
        <FormModal
          title="Add New Job Title"
          onCancel={() => {
            setShowNewJobTitleModal(false);
            setNewJobTitleName("");
            setJobTitleTarget(null);
          }}
          onSubmit={(e) => {
            e.preventDefault();
            handleCreateJobTitle();
          }}
          fields={[
            {
              label: "Job Title Name",
              type: "text",
              value: newJobTitleName,
              onChange: (e) => setNewJobTitleName(e.target.value),
              placeholder: "Enter job title name...",
              required: true,
            },
          ]}
        />
      )}

      {/* People Section */}
      {(() => {
        const peopleCollapsed = !!collapsedSections['people'];
        const internalCollapsed = !!collapsedSections['people.internal'];
        const externalCollapsed = !!collapsedSections['people.external'];
        const showQuickAdd = canInvitePeople && viewMode === 'grid' && !searchQuery;

        return (
          <>
            <SectionHeader
              title="People"
              count={realEmployeesFiltered.length + contractorEmployeesFiltered.length}
              collapsed={peopleCollapsed}
              onToggleCollapse={() => toggleSection('people')}
            />
            {!peopleCollapsed && (
              <div className={peopleStyles.sectionBody}>
                {peopleSearchHidesAll ? (
                  <p className={peopleStyles.emptySubsection}>No people match "{searchQuery}".</p>
                ) : (
                  <>
                    {/* Internal */}
                    <div className={peopleStyles.subsection}>
                      <SubsectionHeader
                        label="Internal"
                        count={realEmployeesFiltered.length}
                        collapsed={internalCollapsed}
                        onToggleCollapse={() => toggleSection('people.internal')}
                      />
                      {!internalCollapsed && (
                        viewMode === 'list' ? (
                          realEmployeesFiltered.length > 0 ? (
                            <div className={peopleStyles.listTable}>
                              <PersonListHeader />
                              {realEmployeesFiltered.map((u) => (
                                <PersonRow
                                  key={u.user_id}
                                  employee={u}
                                  onClick={() => openEmployeeDetail(u)}
                                  onAddContactInfo={(emp) => editEmployeeDirect(emp)}
                                  actions={buildPersonActions(u)}
                                />
                              ))}
                            </div>
                          ) : (
                            <p className={peopleStyles.emptySubsection}>
                              {searchQuery ? `No internal employees match "${searchQuery}".` : 'No internal employees yet.'}
                            </p>
                          )
                        ) : (
                          <div className={peopleStyles.cardGrid}>
                            {realEmployeesFiltered.map((u) => (
                              <PersonCompactCard
                                key={u.user_id}
                                employee={u}
                                onClick={() => openEmployeeDetail(u)}
                                onAddContactInfo={(emp) => editEmployeeDirect(emp)}
                              />
                            ))}
                            {showQuickAdd && (
                              <button
                                type="button"
                                className={peopleStyles.quickAddTile}
                                onClick={() => setShowInviteForm(true)}
                              >
                                <span className={peopleStyles.quickAddIcon}>＋</span>
                                Add employee
                              </button>
                            )}
                          </div>
                        )
                      )}
                    </div>

                    {/* External */}
                    <div className={peopleStyles.subsection}>
                      <SubsectionHeader
                        label="External"
                        count={contractorEmployeesFiltered.length}
                        collapsed={externalCollapsed}
                        onToggleCollapse={() => toggleSection('people.external')}
                      />
                      {!externalCollapsed && (
                        viewMode === 'list' ? (
                          contractorEmployeesFiltered.length > 0 ? (
                            <div className={peopleStyles.listTable}>
                              <PersonListHeader />
                              {contractorEmployeesFiltered.map((u) => (
                                <PersonRow
                                  key={u.user_id}
                                  employee={u}
                                  onClick={() => openEmployeeDetail(u)}
                                  onAddContactInfo={(emp) => editEmployeeDirect(emp)}
                                  actions={buildPersonActions(u)}
                                />
                              ))}
                            </div>
                          ) : (
                            <p className={peopleStyles.emptySubsection}>
                              {searchQuery ? `No external contractors match "${searchQuery}".` : 'No external contractors yet.'}
                            </p>
                          )
                        ) : (
                          <div className={peopleStyles.cardGrid}>
                            {contractorEmployeesFiltered.map((u) => (
                              <PersonCompactCard
                                key={u.user_id}
                                employee={u}
                                onClick={() => openEmployeeDetail(u)}
                                onAddContactInfo={(emp) => editEmployeeDirect(emp)}
                              />
                            ))}
                            {showQuickAdd && (
                              <button
                                type="button"
                                className={peopleStyles.quickAddTile}
                                onClick={() => setShowPlaceholderModal(true)}
                              >
                                <span className={peopleStyles.quickAddIcon}>＋</span>
                                Add contractor
                              </button>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        );
      })()}

      {/* Resources Section */}
      {canViewResources && (() => {
        const resourcesCollapsed = !!collapsedSections['resources'];
        const toolsCollapsed = !!collapsedSections['resources.tools'];
        const vehiclesCollapsed = !!collapsedSections['resources.vehicles'];
        const showQuickAdd = canEditResources && viewMode === 'grid' && !searchQuery;

        return (
          <>
            <SectionHeader
              title="Resources"
              count={toolResourcesFiltered.length + vehicleResourcesFiltered.length}
              collapsed={resourcesCollapsed}
              onToggleCollapse={() => toggleSection('resources')}
            />
            {!resourcesCollapsed && (
              <div className={peopleStyles.sectionBody}>
                {resourcesSearchHidesAll ? (
                  <p className={peopleStyles.emptySubsection}>No resources match "{searchQuery}".</p>
                ) : (
                  <>
                    {/* Tools / Equipment */}
                    <div className={peopleStyles.subsection}>
                      <SubsectionHeader
                        label="Tools / Equipment"
                        count={toolResourcesFiltered.length}
                        collapsed={toolsCollapsed}
                        onToggleCollapse={() => toggleSection('resources.tools')}
                      />
                      {!toolsCollapsed && (
                        viewMode === 'list' ? (
                          toolResourcesFiltered.length > 0 ? (
                            <div className={peopleStyles.listTable}>
                              <ResourceListHeader />
                              {toolResourcesFiltered.map((resource) => (
                                <ResourceRow
                                  key={resource.resource_id}
                                  resource={resource}
                                  onClick={() => openResourceDetail(resource)}
                                  actions={buildResourceActions(resource)}
                                />
                              ))}
                            </div>
                          ) : (
                            <p className={peopleStyles.emptySubsection}>
                              {searchQuery ? `No tools match "${searchQuery}".` : 'No tools or equipment yet.'}
                            </p>
                          )
                        ) : (
                          <div className={peopleStyles.cardGrid}>
                            {toolResourcesFiltered.map((resource) => (
                              <ResourceCompactCard
                                key={resource.resource_id}
                                resource={resource}
                                onClick={() => openResourceDetail(resource)}
                              />
                            ))}
                            {showQuickAdd && (
                              <button
                                type="button"
                                className={peopleStyles.quickAddTile}
                                onClick={() => {
                                  setResourceForm((prev) => ({ ...prev, type: 'tool' }));
                                  setShowResourceModal(true);
                                }}
                              >
                                <span className={peopleStyles.quickAddIcon}>＋</span>
                                Add tool or equipment
                              </button>
                            )}
                            {toolResourcesFiltered.length === 0 && !showQuickAdd && (
                              <p className={peopleStyles.emptySubsection}>No tools or equipment yet.</p>
                            )}
                          </div>
                        )
                      )}
                    </div>

                    {/* Vehicles */}
                    <div className={peopleStyles.subsection}>
                      <SubsectionHeader
                        label="Vehicles"
                        count={vehicleResourcesFiltered.length}
                        collapsed={vehiclesCollapsed}
                        onToggleCollapse={() => toggleSection('resources.vehicles')}
                      />
                      {!vehiclesCollapsed && (
                        viewMode === 'list' ? (
                          vehicleResourcesFiltered.length > 0 ? (
                            <div className={peopleStyles.listTable}>
                              <ResourceListHeader />
                              {vehicleResourcesFiltered.map((resource) => (
                                <ResourceRow
                                  key={resource.resource_id}
                                  resource={resource}
                                  onClick={() => openResourceDetail(resource)}
                                  actions={buildResourceActions(resource)}
                                />
                              ))}
                            </div>
                          ) : (
                            <p className={peopleStyles.emptySubsection}>
                              {searchQuery ? `No vehicles match "${searchQuery}".` : 'No vehicles yet.'}
                            </p>
                          )
                        ) : (
                          <div className={peopleStyles.cardGrid}>
                            {vehicleResourcesFiltered.map((resource) => (
                              <ResourceCompactCard
                                key={resource.resource_id}
                                resource={resource}
                                onClick={() => openResourceDetail(resource)}
                              />
                            ))}
                            {showQuickAdd && (
                              <button
                                type="button"
                                className={peopleStyles.quickAddTile}
                                onClick={() => {
                                  setResourceForm((prev) => ({ ...prev, type: 'vehicle' }));
                                  setShowResourceModal(true);
                                }}
                              >
                                <span className={peopleStyles.quickAddIcon}>＋</span>
                                Register fleet vehicle
                              </button>
                            )}
                            {vehicleResourcesFiltered.length === 0 && !showQuickAdd && (
                              <p className={peopleStyles.emptySubsection}>No vehicles yet.</p>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        );
      })()}

      {/* Resource Create Modal */}
      {showResourceModal && (
        <FormModal
          title="Create Resource"
          onCancel={() => {
            setShowResourceModal(false);
            resetResourceForm();
          }}
          onSubmit={handleCreateResource}
          fields={getResourceFormFields(resourceForm, setResourceForm)}
        />
      )}

      {/* Resource Edit Modal */}
      {editingResource && canEditResources && (
        <FormModal
          title="Edit Resource"
          onCancel={() => setEditingResource(null)}
          onSubmit={handleUpdateResource}
          fields={getResourceFormFields(editingResource, setEditingResource)}
        />
      )}

      {/* Resource Detail Modal */}
      {showResourceDetailModal && selectedResource && (
        <DetailsModal
          title="Resource Details"
          onClose={() => {
            setShowResourceDetailModal(false);
            setSelectedResource(null);
          }}
          onEdit={canEditResources ? handleEditResourceFromDetail : null}
          onDelete={
            selectedResource.is_active
              ? (canDeleteResources ? () => setShowArchiveConfirm(true) : null)
              : null
          }
          deleteLabel="Archive Resource"
          editLabel="Edit Resource"
        >
          {/* Resource Header */}
          <div className={detailStyles.detailSection}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '20px',
              padding: '20px',
              background: selectedResource.type === 'vehicle' 
                ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                : selectedResource.type === 'tool' 
                  ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                  : 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
              borderRadius: '12px',
              marginBottom: '8px'
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '12px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
                flexShrink: 0
              }}>
                {selectedResource.type === 'vehicle' ? '🚗' : selectedResource.type === 'tool' ? '🔧' : '📦'}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '1.35rem', fontWeight: '600', color: 'white' }}>
                  {selectedResource.name}
                </h3>
                <p style={{ margin: '6px 0 0', color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
                  {getResourceTypeLabel(selectedResource.type)}
                </p>
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                backgroundColor: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '20px'
              }}>
                <span style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  backgroundColor: selectedResource.is_active ? getAvailabilityColor(selectedResource.availability_status) : '#ef4444'
                }} />
                <span style={{ color: 'white', fontSize: '0.85rem' }}>
                  {selectedResource.is_active ? getAvailabilityLabel(selectedResource.availability_status) : 'Archived'}
                </span>
              </div>
            </div>
          </div>

          {!selectedResource.is_active && canDeleteResources && (
            <div style={{ margin: '0 0 12px', textAlign: 'center' }}>
              <button
                onClick={() => handleRestoreResource(selectedResource)}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#16a34a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: '500'
                }}
              >
                Restore Resource
              </button>
            </div>
          )}

          {/* General Details */}
          <div className={detailStyles.detailSection}>
            <h4 className={detailStyles.sectionTitle}>Details</h4>
            {selectedResource.description && (
              <div className={detailStyles.detailRow}>
                <span className={detailStyles.detailLabel}>Description</span>
                <span className={detailStyles.detailValue}>{selectedResource.description}</span>
              </div>
            )}
          </div>

          {/* Vehicle Details */}
          {selectedResource.type === 'vehicle' && (
            <div className={detailStyles.detailSection}>
              <h4 className={detailStyles.sectionTitle}>Vehicle Details</h4>
              <div className={detailStyles.detailRow}>
                <span className={detailStyles.detailLabel}>Registration</span>
                <span className={`${detailStyles.detailValue} ${!selectedResource.registration_number ? detailStyles.empty : ''}`}>
                  {selectedResource.registration_number || '—'}
                </span>
              </div>
              <div className={detailStyles.detailRow}>
                <span className={detailStyles.detailLabel}>Vehicle Type</span>
                <span className={`${detailStyles.detailValue} ${!selectedResource.vehicle_type ? detailStyles.empty : ''}`}>
                  {selectedResource.vehicle_type || '—'}
                </span>
              </div>
              <div className={detailStyles.detailRow}>
                <span className={detailStyles.detailLabel}>Capacity</span>
                <span className={`${detailStyles.detailValue} ${!selectedResource.capacity ? detailStyles.empty : ''}`}>
                  {selectedResource.capacity || '—'}
                </span>
              </div>
              <div className={detailStyles.detailRow}>
                <span className={detailStyles.detailLabel}>Insurance Expiry</span>
                <span className={`${detailStyles.detailValue} ${!selectedResource.insurance_expiry ? detailStyles.empty : ''}`}>
                  {selectedResource.insurance_expiry ? new Date(selectedResource.insurance_expiry).toLocaleDateString() : '—'}
                </span>
              </div>
              <div className={detailStyles.detailRow}>
                <span className={detailStyles.detailLabel}>Inspection Expiry</span>
                <span className={`${detailStyles.detailValue} ${!selectedResource.inspection_expiry ? detailStyles.empty : ''}`}>
                  {selectedResource.inspection_expiry ? new Date(selectedResource.inspection_expiry).toLocaleDateString() : '—'}
                </span>
              </div>
            </div>
          )}

          {/* Accounting */}
          <div className={detailStyles.detailSection}>
            <h4 className={detailStyles.sectionTitle}>Accounting</h4>
            <div className={detailStyles.detailRow}>
              <span className={detailStyles.detailLabel}>Asset Value</span>
              <span className={`${detailStyles.detailValue} ${!selectedResource.asset_value ? detailStyles.empty : ''}`}>
                {selectedResource.asset_value ? (
                  <span style={{ backgroundColor: '#dcfce7', color: '#166534', padding: '4px 10px', borderRadius: '6px', fontWeight: '500', fontSize: '0.9rem' }}>
                    {formatMoney(selectedResource.asset_value)}
                  </span>
                ) : '—'}
              </span>
            </div>
            <div className={detailStyles.detailRow}>
              <span className={detailStyles.detailLabel}>Running Cost</span>
              <span className={`${detailStyles.detailValue} ${!selectedResource.running_cost ? detailStyles.empty : ''}`}>
                {selectedResource.running_cost ? formatMoney(selectedResource.running_cost) : '—'}
              </span>
            </div>
            <div className={detailStyles.detailRow}>
              <span className={detailStyles.detailLabel}>Depreciation Rate</span>
              <span className={`${detailStyles.detailValue} ${!selectedResource.depreciation_rate ? detailStyles.empty : ''}`}>
                {selectedResource.depreciation_rate ? `${selectedResource.depreciation_rate}%` : '—'}
              </span>
            </div>
            <div className={detailStyles.detailRow}>
              <span className={detailStyles.detailLabel}>Cost Center</span>
              <span className={`${detailStyles.detailValue} ${!selectedResource.cost_center ? detailStyles.empty : ''}`}>
                {selectedResource.cost_center || '—'}
              </span>
            </div>
          </div>
        </DetailsModal>
      )}

      {/* Archive Confirmation */}
      {showArchiveConfirm && selectedResource && (
        <ConfirmDialog
          message={`Are you sure you want to archive "${selectedResource.name}"? It will be excluded from active workflows but can be restored later.`}
          confirmText="Archive"
          cancelText="Cancel"
          onConfirm={handleArchiveResource}
          onCancel={() => setShowArchiveConfirm(false)}
        />
      )}
      </div>
    </div>
  );
}
